import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Equipment } from '@/types/equipment';

// ============================================================================
// useEquipment — TanStack Query hooks for cr.or_equipment_catalog
// ----------------------------------------------------------------------------
// Reads/writes go through public.or_equipment_catalog, a pass-through view.
// Supabase JS only reaches the public schema — see migration
// `public_view_or_equipment_catalog`. RLS on the underlying cr.* table gates
// every query by org_id and OR module entitlement.
//
// Cache strategy:
//   - List query: stale-while-revalidate, 30s stale time
//   - Single query: keyed by id, hydrated from list cache when present
//   - Mutations invalidate both
// ============================================================================

const QK = {
    list: ['equipment', 'list'] as const,
    detail: (id: number) => ['equipment', 'detail', id] as const
};

/** Fetch full equipment list. RLS gates rows automatically. */
export function useEquipmentList() {
    return useQuery({
        queryKey: QK.list,
        queryFn: async (): Promise<Equipment[]> => {
            const { data, error } = await supabase
                .from('or_equipment_catalog')
                .select('*')
                .order('equipment_name', { ascending: true });

            if (error) throw error;
            return (data ?? []) as Equipment[];
        },
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false
    });
}

/** Fetch single equipment row. */
export function useEquipment(id: number | null) {
    return useQuery({
        queryKey: id != null ? QK.detail(id) : ['equipment', 'detail', 'none'],
        queryFn: async (): Promise<Equipment> => {
            if (id == null) throw new Error('No equipment ID provided');
            const { data, error } = await supabase
                .from('or_equipment_catalog')
                .select('*')
                .eq('equipment_id', id)
                .single();

            if (error) throw error;
            return data as Equipment;
        },
        enabled: id != null,
        staleTime: 30 * 1000
    });
}

/** Update equipment — RLS enforces ownership. */
export function useUpdateEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            id,
            patch
        }: {
            id: number;
            patch: Partial<Equipment>;
        }): Promise<Equipment> => {
            const { data, error } = await supabase
                .from('or_equipment_catalog')
                .update({ ...patch, updated_at: new Date().toISOString() })
                .eq('equipment_id', id)
                .select()
                .single();

            if (error) throw error;
            return data as Equipment;
        },
        onSuccess: (updated) => {
            qc.invalidateQueries({ queryKey: QK.list });
            qc.setQueryData(QK.detail(updated.equipment_id), updated);
        }
    });
}

/** Insert new equipment. */
export function useCreateEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (
            row: Partial<Equipment> & {
                equipment_name: string;
                equipment_category: string;
                org_id: string;
            }
        ): Promise<Equipment> => {
            const { data, error } = await supabase
                .from('or_equipment_catalog')
                .insert(row)
                .select()
                .single();

            if (error) throw error;
            return data as Equipment;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.list });
        }
    });
}

/** Soft delete (mark inactive). Hard delete is not exposed in v1. */
export function useDeactivateEquipment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: number): Promise<void> => {
            const { error } = await supabase
                .from('or_equipment_catalog')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('equipment_id', id);

            if (error) throw error;
        },
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: QK.list });
            qc.invalidateQueries({ queryKey: QK.detail(id) });
        }
    });
}
